import React, { useState, useMemo } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Calculator, Zap, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';

/**
 * Quick price estimator for vehicle freight.
 * Gives a realistic price range to reduce friction before form fill-out.
 * Data: indicative ranges based on 2026 market for RoRo/containers.
 */

// Indicative price ranges (EUR) — vehicle type × destination zone
const PRICE_MATRIX = {
  // ZONE 1 = Antilles + Réunion + Guyane + Mayotte
  zone1: {
    moto: [850, 1200],
    scooter: [600, 900],
    quad: [950, 1400],
    voiture: [1500, 2200],
    suv: [1800, 2800],
    utilitaire: [2000, 3000],
    jetski: [1100, 1600],
  },
  // ZONE 2 = Polynésie / Nouvelle-Calédonie / Wallis-Futuna
  zone2: {
    moto: [1400, 1800],
    scooter: [950, 1300],
    quad: [1500, 2100],
    voiture: [2500, 3500],
    suv: [3200, 4500],
    utilitaire: [3500, 5000],
    jetski: [1900, 2700],
  },
  // AFRIQUE
  afrique: {
    moto: [700, 1100],
    scooter: [500, 800],
    quad: [850, 1300],
    voiture: [1200, 1900],
    suv: [1500, 2400],
    utilitaire: [1800, 2800],
    jetski: [950, 1500],
  },
  // MAGHREB
  maghreb: {
    moto: [550, 850],
    scooter: [400, 700],
    quad: [700, 1100],
    voiture: [1000, 1600],
    suv: [1300, 2100],
    utilitaire: [1500, 2400],
    jetski: [800, 1300],
  },
};

const VEHICLE_OPTIONS = [
  { id: 'moto', label: '🏍️ Moto' },
  { id: 'scooter', label: '🛵 Scooter' },
  { id: 'quad', label: '🏁 Quad' },
  { id: 'voiture', label: '🚗 Voiture (berline)' },
  { id: 'suv', label: '🚙 SUV / 4x4' },
  { id: 'utilitaire', label: '🚐 Utilitaire' },
  { id: 'jetski', label: '🚤 Jet-ski' },
];

const DESTINATION_OPTIONS = [
  { id: 'zone1', label: 'Guadeloupe' },
  { id: 'zone1', label: 'Martinique' },
  { id: 'zone1', label: 'La Réunion' },
  { id: 'zone1', label: 'Guyane' },
  { id: 'zone1', label: 'Mayotte' },
  { id: 'zone2', label: 'Polynésie française' },
  { id: 'zone2', label: 'Nouvelle-Calédonie' },
  { id: 'zone2', label: 'Wallis-et-Futuna' },
  { id: 'maghreb', label: 'Maroc' },
  { id: 'maghreb', label: 'Algérie' },
  { id: 'maghreb', label: 'Tunisie' },
  { id: 'afrique', label: 'Sénégal' },
  { id: 'afrique', label: "Côte d'Ivoire" },
  { id: 'afrique', label: 'Cameroun' },
  { id: 'afrique', label: 'Gabon' },
  { id: 'afrique', label: 'Madagascar' },
];

export default function FretPriceSimulator({ defaultVehicle = '', defaultDestination = '' }) {
  const [vehicle, setVehicle] = useState(defaultVehicle);
  const [destinationLabel, setDestinationLabel] = useState(defaultDestination);

  const result = useMemo(() => {
    if (!vehicle || !destinationLabel) return null;
    const dest = DESTINATION_OPTIONS.find(d => d.label === destinationLabel);
    if (!dest) return null;
    const range = PRICE_MATRIX[dest.id]?.[vehicle];
    if (!range) return null;
    return { min: range[0], max: range[1] };
  }, [vehicle, destinationLabel]);

  return (
    <Card className="bg-gradient-to-br from-amber-50 to-orange-50 border-2 border-amber-200 shadow-lg" data-testid="fret-price-simulator">
      <CardContent className="p-6 md:p-8">
        <div className="flex items-center gap-3 mb-5">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center">
            <Calculator className="w-6 h-6 text-white" />
          </div>
          <div>
            <h3 className="font-heading font-bold text-xl text-slate-900">Estimation rapide</h3>
            <p className="text-sm text-slate-600">Obtenez une fourchette de prix en 5 secondes</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Type de véhicule</label>
            <select
              value={vehicle}
              onChange={(e) => setVehicle(e.target.value)}
              className="w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm bg-white"
              data-testid="fret-sim-vehicle"
            >
              <option value="">Choisir...</option>
              {VEHICLE_OPTIONS.map(v => <option key={v.id} value={v.id}>{v.label}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Destination</label>
            <select
              value={destinationLabel}
              onChange={(e) => setDestinationLabel(e.target.value)}
              className="w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm bg-white"
              data-testid="fret-sim-destination"
            >
              <option value="">Choisir...</option>
              {DESTINATION_OPTIONS.map((d, i) => (
                <option key={`${d.id}-${i}`} value={d.label}>{d.label}</option>
              ))}
            </select>
          </div>
        </div>

        {result ? (
          <div className="bg-white rounded-xl p-5 border-2 border-emerald-200" data-testid="fret-sim-result">
            <div className="flex items-center gap-2 text-emerald-600 text-xs font-bold uppercase tracking-wide mb-2">
              <Zap className="w-3.5 h-3.5" />
              Estimation indicative
            </div>
            <div className="font-heading font-bold text-3xl md:text-4xl text-slate-900 mb-1">
              {result.min} € — {result.max} €
            </div>
            <p className="text-xs text-slate-500 mb-4">
              Tout inclus : transport maritime, dédouanement, assurance. Prix final selon dimensions exactes et options.
            </p>
            <Link to="/fret#devis">
              <Button className="w-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white font-bold">
                Demander un devis précis (gratuit, 24h)
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        ) : (
          <div className="bg-white/50 rounded-xl p-5 border border-dashed border-slate-300 text-center text-sm text-slate-500">
            👆 Sélectionnez votre véhicule et votre destination pour voir le prix
          </div>
        )}
      </CardContent>
    </Card>
  );
}
